import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { initLenis, getLenis } from "./lib/smooth";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import Home from "./pages/Home";
import About from "./pages/About";
import Framework from "./pages/Framework";
import WhatWeDo from "./pages/WhatWeDo";
import Research from "./pages/Research";
import ForHotels from "./pages/ForHotels";
import Partners from "./pages/Partners";
import GetInvolved from "./pages/GetInvolved";
import Contact from "./pages/Contact";
import Admin from "./pages/Admin";
import ArticleProfile from "./pages/ArticleProfile";
import Reviews from "./pages/Reviews";
import Join from "./pages/Join";
import HotelLogin from "./pages/HotelLogin";
import HotelDashboard from "./pages/HotelDashboard";
import HotelAssessment from "./pages/HotelAssessment";
import PaymentResult from "./pages/PaymentResult";
import Hotels, { HotelProfile } from "./pages/Hotels";
import Unsubscribe from "./pages/Unsubscribe";
import Login from "./pages/Login";
import MemberRegister from "./pages/MemberRegister";
import MemberDashboard from "./pages/MemberDashboard";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import MiniAssessment from "./pages/MiniAssessment";
import AuthCallback from "./pages/AuthCallback";
import { PrivacyPage, TermsPage, AccessibilityPage } from "./pages/Legal";

const ScrollManager = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        const lenis = getLenis();
        if (lenis) lenis.scrollTo(0, { immediate: true });
        else window.scrollTo(0, 0);
    }, [pathname]);
    return null;
};

const NotFound = () => (
    <main className="grain flex min-h-screen flex-col items-start justify-center bg-[#04150D] px-8">
        <p className="eyebrow-gold">404</p>
        <h1 className="font-display mt-4 text-4xl text-[#FDFBF7] sm:text-5xl">Page not found</h1>
        <p className="mt-4 max-w-md text-[#FDFBF7]/70">
            The page you are looking for does not exist or has been moved.
        </p>
        <Link to="/" data-testid="notfound-home-link" className="btn-gold mt-8">
            Back to Home
        </Link>
    </main>
);

function App() {
    const started = useRef(false);
    useEffect(() => {
        if (started.current) return;
        started.current = true;
        initLenis();
    }, []);

    return (
        <div className="App">
            <BrowserRouter>
                <ScrollManager />
                <Navbar />
                <AppRoutes />
                <Footer />
                <Toaster richColors position="top-right" />
            </BrowserRouter>
        </div>
    );
}

const AppRoutes = () => {
    const location = useLocation();
    // Google OAuth returns with #session_id in the URL fragment — process it before any route renders.
    // Must read location.hash (reactive), not window.location.hash (stale after history.replaceState clears it).
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/framework" element={<Framework />} />
                    <Route path="/what-we-do" element={<WhatWeDo />} />
                    <Route path="/research" element={<Research />} />
                    <Route path="/research/organization-profile" element={<ArticleProfile />} />
                    <Route path="/reviews" element={<Reviews />} />
                    <Route path="/join" element={<Join />} />
                    <Route path="/hotel/login" element={<HotelLogin />} />
                    <Route path="/hotel/dashboard" element={<HotelDashboard />} />
                    <Route path="/hotel/assessment" element={<HotelAssessment />} />
                    <Route path="/hotel/payment/success" element={<PaymentResult />} />
                    <Route path="/hotel/payment/cancel" element={<PaymentResult />} />
                    <Route path="/hotels" element={<Hotels />} />
                    <Route path="/hotels/:hotelId" element={<HotelProfile />} />
                    <Route path="/unsubscribe" element={<Unsubscribe />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<MemberRegister />} />
                    <Route path="/member/dashboard" element={<MemberDashboard />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/sustainability-check" element={<MiniAssessment />} />
                    <Route path="/for-hotels" element={<ForHotels />} />
                    <Route path="/partners" element={<Partners />} />
                    <Route path="/get-involved" element={<GetInvolved />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/accessibility" element={<AccessibilityPage />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
    );
}

export default App;
